import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  OwnerDiscount,
  OwnerEvent,
  clubsBookingApi,
} from '@/api/clubs-booking';
import {
  AdminDataTable,
  AdminSection,
  adminPrimaryButtonClassName,
  adminTableCellClassName,
  adminTableClassName,
  adminTableHeadCellClassName,
  adminToolbarClassName,
} from '@/components/admin/AdminLayout';
import AdminMobileRowActions from '@/components/admin/AdminMobileRowActions';
import AdminShell from '@/components/admin/AdminShell';
import TablePagination from '@/components/common/TablePagination';
import { formatCurrencyFromCents } from '@/utils/currency';

const DiscountsPage = () => {
  const [discounts, setDiscounts] = useState<OwnerDiscount[]>([]);
  const [discountsTotal, setDiscountsTotal] = useState(0);
  const [events, setEvents] = useState<OwnerEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(7);

  const eventTitleById = useMemo(() => {
    return new Map(events.map((event) => [event._id, event.title]));
  }, [events]);

  const getErrorMessage = useCallback((err: any, fallback: string) => {
    if (err?.response?.status === 404) {
      return 'Discounts API endpoint is unavailable. Restart the API so the new discounts routes are loaded.';
    }
    const messageText = err?.response?.data?.message;
    return Array.isArray(messageText) ? messageText.join(', ') : messageText || err?.message || fallback;
  }, []);

  const loadDiscounts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const discountData = await clubsBookingApi.listMyDiscounts({
        page,
        limit: pageSize,
      });
      setDiscounts(discountData.items || []);
      setDiscountsTotal(Number(discountData.total || 0));
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load discounts'));
    } finally {
      setLoading(false);
    }
  }, [getErrorMessage, page, pageSize]);

  const loadEvents = useCallback(async () => {
    try {
      const eventData = await clubsBookingApi.listMyEvents(undefined, {
        page: 1,
        limit: 500,
      });
      setEvents(eventData.items || []);
    } catch {
      setEvents([]);
    }
  }, []);

  useEffect(() => {
    void loadDiscounts();
  }, [loadDiscounts]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  const summarize = (discount: OwnerDiscount) => {
    if (discount.type === 'multi_attendee') {
      const minAttendees = discount.minAttendees || 2;
      return discount.amountType === 'percentage'
        ? `${discount.amountValue}% off each additional attendee from attendee ${minAttendees}`
        : `${formatCurrencyFromCents(discount.amountValue, 'GBP')} off each additional attendee from attendee ${minAttendees}`;
    }

    if (discount.type === 'multi_purchase') {
      const minSessions = discount.minSessionsPerAttendee || 3;
      return discount.amountType === 'percentage'
        ? `${discount.amountValue}% off with ${minSessions}+ sessions`
        : `${formatCurrencyFromCents(discount.amountValue, 'GBP')} off per attendee with ${minSessions}+ sessions`;
    }

    return discount.amountType === 'percentage'
      ? `${discount.amountValue}% code discount`
      : `${formatCurrencyFromCents(discount.amountValue, 'GBP')} code discount`;
  };

  const onToggleEnabled = async (discount: OwnerDiscount) => {
    try {
      await clubsBookingApi.updateDiscount(discount._id, { enabled: !discount.enabled });
      await loadDiscounts();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Could not update discount status'));
    }
  };

  const onDelete = async (discount: OwnerDiscount) => {
    const ok = window.confirm(`Delete discount "${discount.name}"?`);
    if (!ok) return;

    try {
      await clubsBookingApi.deleteDiscount(discount._id);
      await loadDiscounts();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Could not delete discount'));
    }
  };

  return (
    <AdminShell title="Discounts" subtitle="Create code and automatic discount rules for your activities">
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <AdminSection>
        <div className={adminToolbarClassName}>
          <h2 className="text-2xl font-semibold">Discount list</h2>
          <Link to="/discounts/new" className={adminPrimaryButtonClassName}>
            Create
          </Link>
        </div>

        <AdminDataTable>
          <table className={`${adminTableClassName} min-w-[780px] sm:min-w-[900px]`}>
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className={adminTableHeadCellClassName}>Name</th>
                <th className={adminTableHeadCellClassName}>Type</th>
                <th className={adminTableHeadCellClassName}>Rule</th>
                <th className={adminTableHeadCellClassName}>Activities</th>
                <th className={adminTableHeadCellClassName}>Status</th>
                <th className={`${adminTableHeadCellClassName} hidden sm:table-cell`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {discounts.map((discount) => (
                <tr key={discount._id}>
                  <td className={adminTableCellClassName}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900">{discount.name}</div>
                        {discount.type === 'code' && discount.code && (
                          <div className="text-xs text-slate-500">Code: {discount.code}</div>
                        )}
                      </div>
                      <AdminMobileRowActions
                        title={discount.name}
                        description="Manage this discount."
                        items={[
                          { label: 'Edit', to: `/discounts/${discount._id}/edit` },
                          {
                            label: discount.enabled ? 'Disable' : 'Enable',
                            onSelect: () => {
                              void onToggleEnabled(discount);
                            },
                          },
                          {
                            label: 'Delete',
                            tone: 'danger',
                            onSelect: () => {
                              void onDelete(discount);
                            },
                          },
                        ]}
                      />
                    </div>
                  </td>
                  <td className={`${adminTableCellClassName} capitalize`}>{discount.type.replace('_', ' ')}</td>
                  <td className={adminTableCellClassName}>{summarize(discount)}</td>
                  <td className={adminTableCellClassName}>
                    {discount.appliesToAllActivities
                      ? 'All activities'
                      : (discount.activityIds || [])
                          .map((activityId) => eventTitleById.get(activityId) || 'Unknown activity')
                          .join(', ')}
                  </td>
                  <td className={adminTableCellClassName}>
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${
                        discount.enabled
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-200 text-slate-700'
                      }`}
                    >
                      {discount.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className={`${adminTableCellClassName} hidden sm:table-cell`}>
                    <div className="flex flex-wrap gap-2">
                      <Link
                        to={`/discounts/${discount._id}/edit`}
                        className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium hover:bg-slate-50"
                      >
                        Edit
                      </Link>
                      <button
                        type="button"
                        className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium hover:bg-slate-50"
                        onClick={() => void onToggleEnabled(discount)}
                      >
                        {discount.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        type="button"
                        className="rounded-md border border-red-200 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                        onClick={() => void onDelete(discount)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {loading && <p className="px-4 py-6 text-sm text-slate-500">Loading discounts...</p>}
          {!loading && discountsTotal === 0 && <p className="px-4 py-6 text-sm text-slate-500">No discounts created yet.</p>}
          {!loading && discountsTotal > 0 && (
            <TablePagination
              totalItems={discountsTotal}
              currentPage={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          )}
        </AdminDataTable>
      </AdminSection>
    </AdminShell>
  );
};

export default DiscountsPage;


